import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LockKey, FileArrowDown, FilePdf, Eye, Archive, Warning } from '@phosphor-icons/react';
import { publicShareApi, withHonorific } from '@/pages/docs/shareApi';
import { jstDate } from '@/utils/jstDate';

const KB = 1024;
const human = (n) => (!n ? '' : n > KB * KB ? `${(n / KB / KB).toFixed(1)} MB` : `${Math.round(n / KB)} KB`);

/** 社外の人が開く画面。ログイン不要・トークンとパスワードだけ。 */
export default function PublicShare() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [session, setSession] = useState('');
  const [share, setShare] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dead, setDead] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    document.title = '共有書類 / Shared documents';
    return () => { document.head.removeChild(m); };
  }, []);

  const unlock = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setError('');
    try {
      const r = await publicShareApi.unlock(token, password);
      setSession(r.session); setShare(r.share);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) setDead(true);
      else setError(err?.response?.data?.detail || 'パスワードが違います / Wrong password');
    } finally { setBusy(false); }
  };

  if (dead) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 px-4">
        <div className="text-center space-y-2" data-testid="share-invalid">
          <Warning size={28} weight="duotone" className="mx-auto text-zinc-400" />
          <p className="text-sm font-semibold text-zinc-700">このリンクは無効です</p>
          <p className="text-[11px] text-zinc-400">This link is no longer valid.</p>
        </div>
      </div>
    );
  }

  if (!share) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 px-4">
        <form onSubmit={unlock} className="w-full max-w-sm bg-white rounded-xl border border-zinc-200 p-6 space-y-4"
          data-testid="share-password-form">
          <div className="flex items-center gap-2">
            <LockKey size={18} weight="duotone" className="text-teal-700" />
            <h1 className="text-base font-bold text-zinc-900">株式会社センスビー</h1>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            共有された書類を見るには、お伝えしたパスワードを入力してください。<br />
            <span className="text-zinc-400">Enter the password you received to view the shared documents.</span>
          </p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus
            data-testid="share-password-input" placeholder="パスワード / Password"
            className="w-full border border-zinc-300 rounded px-3 py-2 text-sm outline-none focus:border-teal-500" />
          {error && <p className="text-[11px] text-rose-600" data-testid="share-password-error">{error}</p>}
          <button type="submit" disabled={busy || !password} data-testid="share-password-submit"
            className="w-full py-2 rounded bg-teal-700 text-white text-sm font-semibold disabled:opacity-40">
            {busy ? '確認中…' : '開く / Open'}
          </button>
        </form>
      </div>
    );
  }

  const files = share.files || [];
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 px-4 py-3">
        <h1 className="text-base font-bold text-zinc-900">株式会社センスビー</h1>
        <p className="text-[11px] text-zinc-500">
          {withHonorific(share.recipient)} ・ 有効期限 {jstDate(share.expires_at)}
          {share.downloads_left !== null && share.downloads_left !== undefined
            ? ` ・ ダウンロード残り ${share.downloads_left}回` : ''}
        </p>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {share.note && <p className="text-[12px] text-zinc-600">{share.note}</p>}

        {files.length > 1 && share.allow_download && (
          <a href={publicShareApi.zipUrl(token, session)} data-testid="share-zip-all"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-700 text-white text-[12px] font-semibold hover:bg-teal-800">
            <Archive size={14} weight="duotone" />すべてZIPでダウンロード / Download all as ZIP
          </a>
        )}

        <ul className="bg-white rounded-lg border border-zinc-200 divide-y divide-zinc-100"
          data-testid="share-file-list">
          {files.map((f) => (
            <li key={f.document_id} className="flex items-center gap-3 px-3 py-2.5"
              data-testid={`share-file-${f.document_id}`}>
              <FilePdf size={16} weight="duotone" className="shrink-0 text-zinc-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] text-zinc-800 truncate">{f.title || f.filename}</span>
                <span className="block text-[10px] text-zinc-400 font-mono truncate">{f.filename} {human(f.size)}</span>
              </span>
              <button type="button" onClick={() => setPreview(f)} data-testid={`share-preview-${f.document_id}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-teal-500">
                <Eye size={11} />表示 / View
              </button>
              {share.allow_download && (
                <a href={publicShareApi.fileUrl(token, f.document_id, session, true)}
                  data-testid={`share-download-${f.document_id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-teal-500">
                  <FileArrowDown size={11} />保存 / Download
                </a>
              )}
            </li>
          ))}
        </ul>

        <p className="text-[10px] text-zinc-400">
          このページは当社から共有されたものです。アクセスは記録されます。
          <span className="block">This page was shared by Sensbey. Access is logged.</span>
        </p>
      </main>

      {preview && (
        <SharePreviewModal token={token} session={session} file={preview}
          allowDownload={share.allow_download} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

const PDF_MIME = 'application/pdf';
const canShowInline = (mime, name) => {
  const m = (mime || '').toLowerCase();
  if (m === PDF_MIME || m.startsWith('image/') || m.startsWith('text/')) return true;
  return /\.(pdf|png|jpe?g|gif|webp|bmp|svg|txt|csv)$/i.test(name || '');
};

/** 社外向けのプレビュー。APIを iframe に入れず、取得した中身を blob にして表示する。 */
function SharePreviewModal({ token, session, file, allowDownload, onClose }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | unsupported | error
  const name = file.filename || '';
  // 1 · 社内で保存した向きをそのまま使う（1ページ目・2026-11-05）
  const shareDeg = Number((file.page_rotations || {})['1'] || 0);
  const mime = (file.mime_type || '').toLowerCase();
  const isPdf = mime === PDF_MIME || /\.pdf$/i.test(name);
  const isImage = mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);

  useEffect(() => {
    if (!canShowInline(mime, name)) { setState('unsupported'); return undefined; }
    let objectUrl = null;
    let alive = true;
    publicShareApi.fileBlob(token, file.document_id, session)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(isPdf ? blob.slice(0, blob.size, PDF_MIME) : blob);
        setUrl(objectUrl);
        setState('ready');
      })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [token, session, file.document_id, mime, name, isPdf]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" data-testid="share-preview-modal">
      <div className="shrink-0 bg-zinc-900 border-b border-white/10 px-3 py-2 flex items-center gap-3"
        data-testid="share-preview-header">
        <span className="hidden sm:block shrink-0 text-[12px] font-semibold text-white">株式会社センスビー</span>
        <span className="min-w-0 flex-1 text-[12px] text-zinc-300 truncate" title={file.title || name}
          data-testid="share-preview-name">{file.title || name}</span>
        {allowDownload && (
          <a href={publicShareApi.fileUrl(token, file.document_id, session, true)}
            data-testid="share-preview-download"
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-white/25 text-[11px] text-white hover:bg-white/10">
            <FileArrowDown size={12} />保存 / Download
          </a>
        )}
        <button type="button" onClick={onClose} data-testid="share-preview-close"
          className="shrink-0 px-2 py-1 rounded border border-white/25 text-[11px] text-white hover:bg-white/10">
          閉じる / Close
        </button>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center p-3">
        {state === 'loading' && (
          <p className="text-[12px] text-zinc-300" data-testid="share-preview-loading">読み込み中 / Loading…</p>
        )}
        {state === 'ready' && isImage && (
          <img src={url} alt={name} data-testid="share-preview-image"
            style={shareDeg ? { transform: `rotate(${shareDeg}deg)` } : undefined}
            className="max-h-full max-w-full object-contain bg-white rounded" />
        )}
        {state === 'ready' && !isImage && (
          <iframe title="preview" src={url} data-testid="share-preview-frame"
            style={shareDeg ? { transform: `rotate(${shareDeg}deg)` } : undefined}
            className="w-full h-full bg-white rounded" />
        )}
        {(state === 'unsupported' || state === 'error') && (
          <div className="max-w-sm bg-white rounded-xl px-5 py-6 text-center space-y-2"
            data-testid="share-preview-unsupported">
            <FileArrowDown size={26} weight="duotone" className="mx-auto text-zinc-400" />
            <p className="text-[12.5px] font-semibold text-zinc-800">
              {state === 'error' ? 'このファイルを表示できませんでした'
                : 'このファイルはブラウザで表示できません'}
            </p>
            <p className="text-[11px] text-zinc-500">
              ダウンロードしてご確認ください
              <span className="block text-zinc-400">Preview not available, please download</span>
            </p>
            <p className="text-[11px] text-zinc-500 font-mono truncate">{name} {human(file.size)}</p>
            {allowDownload && (
              <a href={publicShareApi.fileUrl(token, file.document_id, session, true)}
                data-testid="share-preview-unsupported-download"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-700 text-white text-[12px] font-semibold hover:bg-teal-800">
                <FileArrowDown size={13} />ダウンロード / Download
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
